/**
 * Resolving the parent plugin a derived skill came from (issue #172).
 *
 * A "derived skill" is a synthesized catalog entry the processor produces from a
 * plugin's embedded skills[] (catalog-builder.ts synthesizeSkillEntries). The child
 * carries no pointer back to its parent, but each parent plugin's skills[].installName
 * is stamped with that child skill's own marketplace installName
 * (catalog-builder.ts enrichSkillsWithInstallNames). These helpers invert that link.
 *
 * Consequence worth knowing: when an entry in a parent's `skills[]` has no `installName`
 * (not the parent entry itself — `:39` makes that impossible), this yields no parent for
 * that child, so the CHILD's own detail page loses its breadcrumb. That is why the builder
 * keeps a same-repo link wherever one legitimately exists, rather than unlinking whenever
 * the parent's own twin is absent (#547).
 */

import type { MarketplaceEntry } from "../types/index";

/**
 * Build a reverse index: a derived skill's installName → its parent plugin entry.
 *
 * Only same-repo links are kept, so an unrelated entry's detail page never gets
 * labelled with a bogus parent.
 *
 * That check was originally needed because the catalog builder resolved these links
 * through a global, name-keyed index in which standalone records overrode synthesized
 * ones — so a plugin's embedded skill could point at an unrelated entry that merely
 * shared its name, possibly in another repo. **That mechanism is gone as of #547:** the
 * builder now derives the link from the parent record or resolves it through a
 * repo-scoped key, so a cross-repo value is no longer representable in a freshly built
 * catalog.
 *
 * The check stays anyway, for the same reason PluginDetailPage keeps its own: rebuilds
 * are webhook-driven, so a catalog published by the pre-#547 builder keeps serving until
 * scripts/rebuild-catalog.ts runs. Removing it before then would re-expose the bug.
 */
export function buildParentIndex(catalog: MarketplaceEntry[]): Map<string, MarketplaceEntry> {
  const entryByInstallName = new Map<string, MarketplaceEntry>();
  for (const entry of catalog) {
    if (entry.installName) entryByInstallName.set(entry.installName, entry);
  }

  const map = new Map<string, MarketplaceEntry>();
  for (const parent of catalog) {
    if (parent.extensionType !== "plugin" || !parent.skills?.length) continue;
    for (const skill of parent.skills) {
      const childInstallName = skill.installName;
      // Skip missing links and any link pointing back at this same plugin
      // (a skill sharing its plugin's name; the builder normally already drops it).
      if (!childInstallName || childInstallName === parent.installName) continue;
      const child = entryByInstallName.get(childInstallName);
      // Require the resolved child to share the parent's repo (see doc comment).
      if (
        !child ||
        child.source !== parent.source ||
        child.orgOrOwner !== parent.orgOrOwner ||
        child.repoName !== parent.repoName
      ) continue;
      // First writer wins on the rare duplicate child installName.
      if (!map.has(childInstallName)) map.set(childInstallName, parent);
    }
  }
  return map;
}

/**
 * Breadcrumb trail for a detail page, derived purely from data: a derived skill's
 * resolved parent plugin → Marketplace › Plugin › Skill; anything else has no
 * ancestor → Marketplace › Name.
 *
 * Deriving the trail from the route/data (rather than an append-only nav-history
 * stack) is what keeps it correct under browser Back/Forward and on deep links —
 * it's a function of the current route, so it cannot drift out of sync. The old
 * nav-state stack pushed a crumb on every forward navigation but never popped on
 * Back, so the parent crumb duplicated and accumulated each time.
 */
export function computeEffectiveBreadcrumbs(
  parentPlugin: MarketplaceEntry | undefined,
): MarketplaceEntry[] {
  return parentPlugin ? [parentPlugin] : [];
}
